import { ErrorBoundary } from '@sentry/react';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';
import { Toaster } from 'sonner';

import { useApi } from '../../api/useApi.hook';
import { Loading } from '../../components/loading/loading.component';
import { ModalsOutlet } from '../../contexts/modalsOutlet.component';
import { NavBlockerContext } from '../../contexts/navBlocker.context';
import { useModals } from '../../hooks/useModals.hook';
import { useNavBlockerProvider } from '../../hooks/useNavBlockerProvider.hook';
import { ScreenOfDeath } from '../components/error/screenOfDeath.component';
import { SessionContext } from '../contexts/session.context';
import { useAnalytics } from '../hooks/useAnalytics.hook';
import LoginModal from '../modals/login/loginModal.component';
import { RouteIndex } from '../routeIndex';
import PersonalDetailsView from './account/personalDetails.view';
import AccountSettingsView from './account/accountSettings.view';
import SetPasswordView from './public/setPassword.view';
import DashboardView from './dashboard/dashboard.view';
import DocumentBankTransferPaymentView from './document/documentBankTransferPayment.view';
import DocumentPaymentReturnView from './document/documentPaymentReturn.view';
import EditDocumentView from './document/editDocument.view';
import NewDocumentView from './document/newDocument.view';
import GoogleCallbackView from './public/googleCallback.view';
import LoginView from './public/login.view';
import AuthView from './public/magic-link.view';
import SignUpView from './public/signUp.view';
import ForgotPasswordView from './public/forgotPassword.view';
import ResetPasswordView from './public/resetPassword.view';
import BankTransferPaymentView from './wallet/bankTransferPayment.view';
import CheckoutView from './wallet/checkout.view';
import CreditPaymentReturnView from './wallet/creditPaymentReturn.view';
import PaymentSuccessView from './wallet/paymentSuccess.view';
import TopUpCreditView from './wallet/topUpCredit.view';
import WalletView from './wallet/wallet.view';

export const PublicRoutes = () => (
    <Routes>
        <Route path={RouteIndex.signUp} element={<SignUpView />} />
        <Route path={RouteIndex.auth} element={<AuthView />} />
        <Route path={RouteIndex.googleCallback} element={<GoogleCallbackView />} />
        <Route path={RouteIndex.forgotPassword} element={<ForgotPasswordView />} />
        <Route path={RouteIndex.resetPassword} element={<ResetPasswordView />} />
        <Route path="/auth/reset-password" element={<ResetPasswordView />} />
        <Route path={'/*'} element={<LoginView />} />
    </Routes>
);


export const ProtectedRoutes = () => (
    <Routes>
        <Route path={RouteIndex.newDocument} element={<NewDocumentView />} />
        <Route path={RouteIndex.documentDetail.edit} element={<EditDocumentView />} />
        <Route path={RouteIndex.documentDetail.editRevision} element={<EditDocumentView />} />
        <Route path={RouteIndex.documentDetail.bankTransferPayment} element={<DocumentBankTransferPaymentView />} />
        <Route path={RouteIndex.documentDetail.paymentReturn} element={<DocumentPaymentReturnView />} />
        <Route path={RouteIndex.accountSettings} element={<AccountSettingsView />} />
        <Route path={RouteIndex.wallet} element={<WalletView />} />
        <Route path={RouteIndex.topUpCredit} element={<TopUpCreditView />} />
        <Route path={RouteIndex.checkout} element={<CheckoutView />} />
        <Route path={RouteIndex.bankTransferPayment} element={<BankTransferPaymentView />} />
        <Route path={RouteIndex.creditPaymentReturn} element={<CreditPaymentReturnView />} />
        <Route path={RouteIndex.paymentSuccess} element={<PaymentSuccessView />} />
        <Route path="/*" element={<DashboardView />} />
    </Routes>
);

export const MainView = () => {
    useAnalytics();

    const api = useApi();
    const modals = useModals();
    const queryClient = useQueryClient();

    const [ hasBeenLoggedIn, setHasBeenLoggedIn ] = useState(api.client.hasSession());
    const [ isLoggedIn, setIsLoggedIn ] = useState(api.client.hasSession());
    const isAuthRoute = location.pathname.startsWith(RouteIndex.auth)
        || location.pathname.startsWith(RouteIndex.resetPassword)
        || location.pathname.startsWith(RouteIndex.forgotPassword)
        || location.pathname.startsWith('/auth/reset-password');

    const myIdentity = useQuery({
        queryKey: ['my-identity'],
        queryFn: async () => {
            return api.v1.identities.getMyIdentity();
        },
        refetchInterval: 1000 * 60 * 2, // 2 minutes (also refreshes tokens as a side effect - important for session expiration)
        enabled: isLoggedIn
    });

    const handleLogout = useCallback(() => {
        api.client.deleteSession();
        location.reload();
    }, []);

    const navBlockerContext = useNavBlockerProvider();

    const reportLoadingTimeout = useCallback(() => {
        Sentry.captureMessage('Loading app takes longer than expected.', {
            extra: {
                hasBeenLoggedIn,
                isLoggedIn,
                sessionId: api.client.getIdentityId(),
                identityId: api.client.getIdentityId(),
                hasAccessToken: api.client.getAccessToken() !== null,
            },
            level: 'debug',
            tags: {
                'category': 'init'
            }
        });
    }, [ hasBeenLoggedIn, isLoggedIn, api ]);

    useEffect(() => {
        const onSessionEstablished = () => {
            setHasBeenLoggedIn(true);
            setIsLoggedIn(true);
            modals.hideModal('session-expired');
            // Invalidate all queries when a new session is established
            queryClient.clear();
        };

        const onSessionExpired = () => {
            setIsLoggedIn(false);
            modals.showModal('session-expired', LoginModal, {});
        };

        const onSessionDeleted = () => {
            setIsLoggedIn(false);
            setHasBeenLoggedIn(false);
            // Clear all cached data when session is deleted
            queryClient.clear();
        };

        api.client.addEventListener('sessionEstablished', onSessionEstablished);
        api.client.addEventListener('sessionExpired', onSessionExpired);
        api.client.addEventListener('sessionDeleted', onSessionDeleted);

        return () => {
            api.client.removeEventListener('sessionEstablished', onSessionEstablished);
            api.client.removeEventListener('sessionExpired', onSessionExpired);
            api.client.removeEventListener('sessionDeleted', onSessionDeleted);
        };
    }, [ api.client, queryClient ]);
    
    const needToSetPassword = myIdentity.data && !myIdentity.data.userFields['passwordConfigured'];
    const needToEnterPersonalDetails = myIdentity.data && !myIdentity.data.userFields['onboardingCompleted'] && !myIdentity.data.userFields['name'];

    return (
        <ErrorBoundary fallback={ScreenOfDeath}>
            <NavBlockerContext.Provider value={navBlockerContext}>
                <BrowserRouter>
                    {(!hasBeenLoggedIn || isAuthRoute)
                        ? (
                            <PublicRoutes />
                        )
                        : (
                            <>
                                {hasBeenLoggedIn && !myIdentity.data && (
                                    <div className='flex items-center justify-center h-screen'>
                                        <Loading
                                            label='Načítání aplikace...'
                                            timeoutMessage='Načítání trvá déle než obvykle, zkontrolujte své připojení k internetu a zkuste obnovit stránku.'
                                            renderTimeoutContent={() => (
                                                <>
                                                    {api.client.hasSession() ? (
                                                        <p className='text-sm mt-1'>Pokud problém přetrvává, zkuste se <button onClick={handleLogout} className='hyperlink'>odhlásit</button>. Případně kontaktujte podporu na <a href="mailto:info@aikorektury.cz" className='hyperlink'>info@aikorektury.cz</a>.</p>
                                                    ) : (
                                                        <p className='text-sm mt-1'>Pokud problém přetrvává, kontaktujte prosím podporu na <a href="mailto:info@aikorektury.cz" className='hyperlink'>info@aikorektury.cz</a>.</p>
                                                    )}
                                                </>
                                            )}
                                            onTimeout={reportLoadingTimeout}
                                        />
                                    </div>
                                )}

                                {hasBeenLoggedIn && myIdentity.data && (
                                    <SessionContext.Provider value={{ isLoggedIn, identity: myIdentity.data }}>
                                                                                {needToSetPassword && (
                                            <SetPasswordView onComplete={() => queryClient.invalidateQueries({ queryKey: ['my-identity'] })} />
                                        )}
                                        {!needToSetPassword && needToEnterPersonalDetails && (
                                            <PersonalDetailsView />
                                        )}
                                        {!needToSetPassword && !needToEnterPersonalDetails && (
                                            <ProtectedRoutes />
                                        )}


                                    </SessionContext.Provider>
                                )}
                            </>
                        )}
                    <Toaster />
                    <ModalsOutlet />
                </BrowserRouter>
            </NavBlockerContext.Provider>
        </ErrorBoundary>
    );
};
